'use client'

import { Spinner } from '@/components/extension/spinner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Minus, Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'

interface ProductInfoAddCartProps {
  isGiveaway?: boolean
  isStopSale?: boolean
  isDisabled?: boolean
}

export const ProductInfoAddCart = ({
  isGiveaway = false,
  isStopSale,
  isDisabled,
}: ProductInfoAddCartProps) => {
  const t = useTranslations('product_detail.title')
  const {
    quantity,
    handleQuantityChange,
    handleIncrement,
    handleDecrement,
    handleKeyDown,
    isPendingAddToCart,
    handleAddToCartClick,
  } = useProductDetailStore()

  const { sortedColorOptions, sizeOptions } = useProductDetailSafe()

  if (isGiveaway) {
    return null
  }

  if (!sortedColorOptions.length && !sizeOptions.length)
    return <DisabledAddCartButton message={t('out_of_stock')} />

  if (isStopSale)
    return <DisabledAddCartButton message={t('stopped_selling')} />

  return (
    <div
      className={cn(
        'relative mt-4 h-13 lg:!mt-10',
        isDisabled && 'pointer-events-none opacity-50'
      )}
    >
      <div className='absolute top-1/2 z-[1] flex h-full w-fit -translate-y-1/2 items-center rounded-full bg-neutral-600 p-4 px-0 text-sm text-light'>
        <button
          className='flex h-10 w-10 items-center justify-center'
          onClick={handleDecrement}
          aria-label={t('decrease_quantity')}
        >
          <Minus size={12} className='text-light' />
        </button>
        <label htmlFor='quantity-input' className='sr-only'>
          {t('quantity')}
        </label>
        <input
          id='quantity-input'
          type='text'
          className='w-9 border-none bg-inherit text-center text-sm outline-none'
          value={quantity}
          onChange={handleQuantityChange}
          onKeyDown={handleKeyDown}
          aria-label={t('quantity')}
        />
        <button
          className='flex h-10 w-10 items-center justify-center'
          onClick={handleIncrement}
          aria-label={t('increase_quantity')}
        >
          <Plus size={12} className='text-light' />
        </button>
      </div>
      <Button
        className='h-full w-full gap-2'
        variant={'black'}
        onClick={() => handleAddToCartClick()}
        id='product-detail-add-cart'
        disabled={isPendingAddToCart}
      >
        <Image
          className='h-4 w-4 max-lg:hidden'
          src='/icons/product/icon-cart.svg'
          width={16}
          height={16}
          alt='add cart icon'
        />
        <p className='font-criteria text-base font-medium normal-case leading-6 max-lg:pl-10'>
          <span className='flex items-center gap-2'>
            {isPendingAddToCart ? t('adding') : t('add_to_cart')}
            {isPendingAddToCart && <Spinner className='h-4 w-4 text-white' />}
          </span>
        </p>
      </Button>
    </div>
  )
}

interface DisabledAddCartButtonProps {
  message: string
}

const DisabledAddCartButton = ({ message }: DisabledAddCartButtonProps) => {
  return (
    <Button
      className='flex w-full items-center gap-2'
      variant={'black'}
      disabled
      id='product-detail-add-cart'
    >
      <Image
        className='h-4 w-4 object-contain'
        src='/icons/product/icon-cart.svg'
        width={16}
        height={16}
        alt='add cart icon'
      />
      <p className='font-criteria text-base font-medium normal-case leading-6'>
        <span className='flex items-center gap-2'>{message}</span>
      </p>
    </Button>
  )
}
